import { Badge, Box, Card, Flex, Heading, Tabs, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { getBadgeColorByStatus, createDataCollectionContext, dbTimeToUserTime, useMobileContext, Tooltip } from '@shared';
import { createColumnHelper } from '@tanstack/react-table';
import smsMessagesService from '../api/services/smsMessagesService';
import { useGetAllAccounts } from '../api/hooks/useAccountSuperadmin';
import { IoCheckmarkCircleOutline, IoCloseCircleOutline, IoSyncCircleOutline } from 'react-icons/io5';
import { useMeta } from '../api/hooks/useMeta';

const columnHelper = createColumnHelper<SmsMessage>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function SmsMessages() {
	const { t } = useTranslation();
	const { isMobile } = useMobileContext();
	const { data: accounts } = useGetAllAccounts();
	const { meta } = useMeta('smsMessagesService');

	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<SmsMessage>(),
		[],
	);

	const columns = [
		columnHelper.accessor('smsMessageID', {
			header: t('general.id'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('accountID', {
			header: t('general.account'),
			enableSorting: false,
			cell: ({ row }) => {
				const accountName = accounts?.data.accounts?.find(account => account.accountID === row.original.accountID)?.name;
				return <Text>{accountName}</Text>;
			},
		}),
		columnHelper.accessor('sender', {
			header: t('general.sender'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('creditCount', {
			header: t('general.used_credits'),
			enableSorting: false,
		}),
		columnHelper.accessor('smsMessageRecipientsCount', {
			header: t('general.receivers'),
			enableSorting: false,
			cell: ({ row }) => {
				return <Text>{row.original.smsMessageRecipientsCount || row.original.expectedRecipientCount}</Text>;
			},
		}),
		columnHelper.display({
			id: 'deliveryDetails',
			header: t('messages.deliver_details'),
			cell: ({ row }) => {
				const deliveredCount = row.original.deliveredCount ?? 0;
				const processingCount = row.original.processingCount ?? 0;
				const failedCount = row.original.failedCount ?? 0;

				if (!row.original.timeSent) return t('general.not_sent');

				return (
					<Flex gap={1}>
						<Tooltip openDelay={150} closeDelay={100} content={t('general.successful_sends')}>
							<Badge minW="45px" colorPalette="green" className="group">
								<IoCheckmarkCircleOutline size={18} />
								{deliveredCount}
							</Badge>
						</Tooltip>

						{processingCount > 0 && (
							<Tooltip openDelay={150} closeDelay={100} content={t('messages.sending_in_progress')}>
								<Badge minW="45px" colorPalette="blue">
									<IoSyncCircleOutline size={18} />
									{processingCount}
								</Badge>
							</Tooltip>
						)}
						{failedCount > 0 && (
							<Tooltip openDelay={150} closeDelay={100} content={t('messages.sending_failed')}>
								<Badge minW="45px" colorPalette="red">
									<IoCloseCircleOutline size={18} />
									{failedCount}
								</Badge>
							</Tooltip>
						)}
					</Flex>
				);
			},
		}),
		columnHelper.accessor('sendingStatus', {
			header: t('general.status'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Badge colorPalette={getBadgeColorByStatus(row.original.sendingStatus)}>{meta?.sendingStatus?.[row.original.sendingStatus]}</Badge>;
			},
			filterFn: 'arrIncludesSomeSensitive',
			meta: {
				filterVariant: 'select',
				filterOptions: [
					{ value: 'completed', label: meta?.sendingStatus?.['completed'] ?? '' },
					{ value: 'error', label: meta?.sendingStatus?.['error'] ?? '' },
					{ value: 'ongoing', label: meta?.sendingStatus?.['ongoing'] ?? '' },
					{ value: 'scheduled', label: meta?.sendingStatus?.['scheduled'] ?? '' },
					{ value: 'waitingToBeSent', label: meta?.sendingStatus?.['waitingToBeSent'] ?? '' },
				] as Array<{ value: SendingStatus; label: string }>,
			},
		}),
		columnHelper.accessor('timeSent', {
			header: t('general.time_sent'),
			sortingFn: 'basic',
			filterFn: 'dateTimeBetween',
			meta: {
				filterVariant: 'dateTime',
				filterLabel: [t('messages.time_sent_after'), t('messages.time_sent_before')],
			},
			cell: ({ row }) => {
				if (row.original.timeSent) {
					return <Text>{dbTimeToUserTime(row.original.timeSent, TIMEZONE)}</Text>;
				}
				return <Text>{t('general.not_sent')}</Text>;
			},
		}),
		columnHelper.accessor('timeScheduledSend', {
			header: t('general.time_scheduled'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (row.original.timeScheduledSend) {
					return <Text>{dbTimeToUserTime(row.original.timeScheduledSend, TIMEZONE)}</Text>;
				}
				return t('general.not_scheduled');
			},
		}),
		columnHelper.accessor('createdFrom', {
			header: t('general.created_from'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{t(`general.${row.original.createdFrom}`)}</Text>;
			},
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeModified) {
					return null;
				}
				return <Text>{dbTimeToUserTime(row.original.timeModified, TIMEZONE)}</Text>;
			},
		}),
	];

	return (
		<Flex flexDirection={'column'} gap={5} width={'100%'} alignItems={'center'} height="100%">
			{!isMobile ? (
				<Card.Root maxWidth="2000px" width="100%">
					<Card.Body>
						<Heading>{t('general.sent_messages')}</Heading>
					</Card.Body>
				</Card.Root>
			) : null}

			<Tabs.Root
				maxWidth="2000px"
				variant={'line'}
				size="lg"
				colorPalette={'brand.primary'}
				width={'100%'}
				display={'flex'}
				flexDirection={'column'}
				height={'100%'}
				defaultValue={'messageHistory'}
				lazyMount
				unmountOnExit
			>
				<Tabs.List flexWrap={'wrap'} display={isMobile ? 'contents' : 'flex'}>
					<Tabs.Trigger value="messageHistory" fontWeight={500}>
						{t('general.message_history')}
					</Tabs.Trigger>
					<Tabs.Trigger value="apiMessageHistory" fontWeight={500}>
						{t('general.api_message_history')}
					</Tabs.Trigger>
				</Tabs.List>
				<Tabs.Content value="messageHistory" display={'flex'} flex={1}>
					<Flex overflow="auto" width={'100%'} display={'flex'} flexDirection={'column'} height={'100%'} maxW={'2000px'}>
						<Flex display={'flex'} flex={1} overflow={'auto'}>
							<Card.Root flex={1} display="flex">
								<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
									<DataCollectionContextProvider
										customQueryKey="superadminUiMessages"
										dataKey="smsMessages"
										keyAttribute="smsMessageID"
										emptyDataText={t('general.no_messages_found')}
										fetchFn={(...args) => smsMessagesService.getAllResources(...args)}
										queryParams={{
											filters: `sendingStatus:ne:draft|createdFrom:eq:ui`,
											scopeWith: 'creditCount|processingCount|deliveredCount|failedCount|expectedRecipientCount|soleRecipient',
											countRelated: 'smsMessageRecipients',
										}}
										initialItemsPerPage={25}
										columns={columns}
										defaultHiddenColumns={[]}
										isServerSide={true}
										isSelectable={false}
										defaultSort={{ id: 'timeCreated', desc: true }}
									>
										<Flex gap={2} flexWrap="wrap" flexShrink={0}>
											<DataCollectionTableHeader />
										</Flex>
										<Box flex={1} overflow="auto" minH={0}>
											<DataCollectionTable />
										</Box>
										<Flex gap={2} flexShrink={0}>
											<DataCollectionTableFooter />
										</Flex>
									</DataCollectionContextProvider>
								</Card.Body>
							</Card.Root>
						</Flex>
					</Flex>
				</Tabs.Content>
				<Tabs.Content value="apiMessageHistory" display={'flex'} flex={1}>
					<Flex overflow="auto" width={'100%'} display={'flex'} flexDirection={'column'} height={'100%'} maxW={'2000px'}>
						<Flex display={'flex'} flex={1} overflow={'auto'}>
							<Card.Root flex={1} display="flex">
								<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
									<DataCollectionContextProvider
										customQueryKey="superadminApiMessages"
										dataKey="smsMessages"
										keyAttribute="smsMessageID"
										emptyDataText={t('general.no_messages_found')}
										fetchFn={(...args) => smsMessagesService.getAllResources(...args)}
										queryParams={{
											filters: `sendingStatus:ne:draft|createdFrom:eq:api`,
											scopeWith: 'creditCount|processingCount|deliveredCount|failedCount|expectedRecipientCount|soleRecipient',
											countRelated: 'smsMessageRecipients',
										}}
										initialItemsPerPage={25}
										columns={columns}
										defaultHiddenColumns={[]}
										isServerSide={true}
										isSelectable={false}
										defaultSort={{ id: 'timeCreated', desc: true }}
									>
										<Flex gap={2} flexWrap="wrap" flexShrink={0}>
											<DataCollectionTableHeader />
										</Flex>
										<Box flex={1} overflow="auto" minH={0}>
											<DataCollectionTable />
										</Box>
										<Flex gap={2} flexShrink={0}>
											<DataCollectionTableFooter />
										</Flex>
									</DataCollectionContextProvider>
								</Card.Body>
							</Card.Root>
						</Flex>
					</Flex>
				</Tabs.Content>
			</Tabs.Root>
		</Flex>
	);
}
